// useAiDocuments Hook

// Fetches documents from the AI function get_documents endpoint.
// Manages server-side search, filtering, sorting, and pagination state.

import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchAiDocuments } from '../services/aiApi';
import type {
	AiDocument,
	AiDocumentPagination,
	AiDocumentQuery,
	AiDocumentStats,
	DocumentFileType,
	DocumentFolder,
	DocumentSourceType,
	SystemDocument,
} from '../components/pages/ProjectV2/Documents/documentTypes';

// Helpers

/** Format bytes into human-readable file size */
const formatFileSize = (bytes: number): string => {
	if (bytes === 0) return '0 B';
	const k = 1024;
	const sizes = ['B', 'KB', 'MB', 'GB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

/** Map a doc_type string to our FileType enum */
const mapFileType = (docType: string): DocumentFileType => {
	const lower = docType.toLowerCase();
	if (lower === 'pdf') return 'pdf';
	if (lower === 'doc' || lower === 'docx' || lower === 'word') return 'doc';
	if (lower === 'xlsx' || lower === 'xls' || lower === 'excel') return 'xlsx';
	if (lower === 'csv') return 'csv';
	if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'image'].includes(lower)) return 'image';
	return 'other';
};

/** Map a source_type from metadata to our SourceType enum */
const mapSourceType = (sourceType?: string): DocumentSourceType => {
	if (!sourceType) return 'contract';
	const lower = sourceType.toLowerCase();
	const validTypes: DocumentSourceType[] = [
		'activity',
		'beneficiary',
		'contract',
		'invoice',
		'payment',
		'transaction',
	];
	return (validTypes.find((t) => t === lower) as DocumentSourceType) || 'contract';
};

/**
 * Transform an AiDocument from the API into a SystemDocument for the UI.
 * Extracts metadata fields and formats sizes/dates for display.
 */
const transformToSystemDocument = (doc: AiDocument): SystemDocument => {
	const metadata = doc.metadata || {};
	const sizeBytes = Number(metadata.file_size_bytes || 0);

	return {
		id: String(doc.id),
		jobId: doc.job_id || undefined,
		name: doc.filename,
		fileType: mapFileType(doc.doc_type),
		mimeType: (metadata.mime_type as string) || undefined,
		size: formatFileSize(sizeBytes),
		sizeBytes,
		uploadedAt: doc.uploaded_at,
		uploadedBy:
			typeof doc.uploaded_by === 'object' && doc.uploaded_by !== null
				? doc.uploaded_by.displayName
				: String(metadata.uploaded_by_name || `User ${doc.uploaded_by}`),
		sourceType: mapSourceType(metadata.source_type as string),
		sourceId: Number(metadata.source_id || doc.contract_id || 0),
		sourceLabel: doc.source?.name || doc.contract_name || '',
		category: (metadata.category as string) || doc.doc_type || 'other',
		tags: Array.isArray(metadata.tags) ? (metadata.tags as string[]) : [],
		description: (metadata.description as string) || undefined,
		url: (metadata.blob_url as string) || (metadata.url as string) || undefined,
	};
};

// Types

/** Map UI sort keys to API sort columns */
const SORT_KEY_MAP: Record<string, string> = {
	name: 'filename',
	uploadedAt: 'uploaded_at',
	size: 'size',
	sizeBytes: 'size',
	sourceType: 'source_type',
};

export interface UseAiDocumentsParams {
	/** Enable/disable auto-fetching */
	isEnabled?: boolean;
	/** Initial page size */
	pageSize?: number;
	/** Project ID for scoping documents to the current project */
	projectId?: number;
}

export interface UseAiDocumentsReturn {
	/** Transformed documents ready for UI rendering */
	data: SystemDocument[];
	/** Raw API documents */
	rawData: AiDocument[];
	/** Loading state */
	loading: boolean;
	/** Error message */
	error: string | null;
	/** Pagination info from API */
	pagination: AiDocumentPagination | null;
	/** Summary stats from API */
	stats: AiDocumentStats | null;
	/** Folders returned when group_by=folder */
	folders: DocumentFolder[];
	/** Re-fetch with current params */
	refetch: () => Promise<void>;
	/** Fetch with explicit query params (for search/filter/sort/page changes) */
	fetchWithQuery: (query: AiDocumentQuery) => Promise<void>;
}

// Hook

export const useAiDocuments = ({
	isEnabled = true,
	pageSize = 20,
	projectId,
}: UseAiDocumentsParams = {}): UseAiDocumentsReturn => {
	// State
	const [data, setData] = useState<SystemDocument[]>([]);
	const [rawData, setRawData] = useState<AiDocument[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [pagination, setPagination] = useState<AiDocumentPagination | null>(null);
	const [stats, setStats] = useState<AiDocumentStats | null>(null);
	const [folders, setFolders] = useState<DocumentFolder[]>([]);

	// Keep track of the last query to support refetch
	const lastQueryRef = useRef<AiDocumentQuery>({ page: 1, page_size: pageSize });

	// Fetch documents from AI API
	const fetchWithQuery = useCallback(
		async (query: AiDocumentQuery) => {
			// Inject project_id into every query for project isolation
			const scopedQuery = projectId ? { ...query, project_id: projectId } : query;
			setLoading(true);
			setError(null);
			lastQueryRef.current = scopedQuery;

			try {
				const res = await fetchAiDocuments(scopedQuery);
				const transformed = (res.data || []).map(transformToSystemDocument);
				setData(transformed);
				setRawData(res.data || []);
				setPagination(res.pagination || null);
				setStats(res.stats || null);

				// Transform API folders into UI DocumentFolder[] if present
				if (res.folders) {
					const uiFolders: DocumentFolder[] = res.folders.map((f) => {
						const docs = (f.documents || []).map(transformToSystemDocument);
						const totalBytes = f.total_size_bytes || 0;
						return {
							id: f.id,
							name: f.name,
							sourceType: mapSourceType(f.source_type),
							sourceId: Number(f.source_id) || 0,
							documentCount: f.document_count,
							totalSize: formatFileSize(totalBytes),
							documents: docs,
							lastUpdated: f.last_updated,
						};
					});
					setFolders(uiFolders);
				} else {
					setFolders([]);
				}
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to fetch documents';
				setError(message);
				setData([]);
				setRawData([]);
			} finally {
				setLoading(false);
			}
		},
		[projectId]
	);

	// Refetch with last query
	const refetch = useCallback(async () => {
		await fetchWithQuery(lastQueryRef.current);
	}, [fetchWithQuery]);

	// Initial fetch
	useEffect(() => {
		if (isEnabled) {
			fetchWithQuery({ page: 1, page_size: pageSize });
		}
	}, [isEnabled, pageSize, fetchWithQuery]);

	return {
		data,
		rawData,
		loading,
		error,
		pagination,
		stats,
		folders,
		refetch,
		fetchWithQuery,
	};
};

// Re-export the sort key map for the component
export { SORT_KEY_MAP };
